import { useState } from "react";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/browser";
import {
  Button,
  Chip,
  Divider,
  KeyIcon,
  SecretTextField,
  TextField,
} from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";

import { fedSignIn } from "app/utils/auth";
import { SamlProvidersArraySchema, type SamlProvider } from "app/utils/configs";
import { RelayEnvironment } from "RelayEnvironment";

import { AuthScreenLayout } from "./AuthScreenLayout";
import { signInSchema, type SignInValues } from "./schema";

import type { CognitoConfigRecord } from "bootstrap";

/**
 * SAML providers to offer as SSO buttons, read from the Cognito config in the
 * Relay store. Prefers the `samlProviders` array, falls back to the legacy
 * `saml` dict (keyed by idp_id), and returns [] if neither parses.
 */
// Provider names and IdP ids belong to the customer, so reports carry the shape
// of the fault only — same rule as bootstrap.ts.
function reportSamlFault(message: string, extra: Record<string, unknown>) {
  console.error(message, extra);
  Sentry.captureException(new Error(message), { extra });
}

/** Amplify's `assertUserNotAuthenticated` rejection, matched the way the SDK keys its own errors. */
function isAlreadySignedIn(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "name" in err &&
    err.name === "UserAlreadyAuthenticatedException"
  );
}

function getSamlProviders(cognitoConfig: CognitoConfigRecord): SamlProvider[] {
  if (typeof cognitoConfig?.samlProviders === "string") {
    try {
      const parsed = JSON.parse(cognitoConfig.samlProviders);
      const result = SamlProvidersArraySchema.safeParse(parsed);

      if (result.success && result.data.length > 0) {
        return result.data;
      } else if (!result.success) {
        reportSamlFault("SAML providers validation failed", {
          validationErrors: result.error.errors.map((issue) => ({
            path: issue.path,
            code: issue.code,
            message: issue.message,
          })),
        });
      }
    } catch (error) {
      reportSamlFault("Failed to parse SAML providers configuration", {
        error: String(error),
      });
    }
  }

  // Legacy fallback.
  if (typeof cognitoConfig?.saml === "string") {
    try {
      const samlTargets: Record<string, string> = JSON.parse(
        cognitoConfig.saml,
      );
      return Object.keys(samlTargets).map((idpId) => ({
        idp_id: idpId,
        name: samlTargets[idpId],
        display_name: samlTargets[idpId],
      }));
    } catch (error) {
      reportSamlFault("Failed to parse legacy SAML configuration", {
        error: String(error),
      });
    }
  }

  return [];
}

/** Props for SignInScreen */
interface Props {
  /** Shown above the options, for failures the auth machine never hears about. */
  notice?: string;
}

/**
 * The sign-in screen: SAML SSO buttons above an email & password form. Replaces
 * Amplify's built-in sign-in UI but drives the same auth machine via
 * `useAuthenticator`, so SSO, MFA, and password reset all still work.
 */
export function SignInScreen({ notice }: Props) {
  const { submitForm, toForgotPassword, error, isPending } = useAuthenticator(
    (context) => [
      context.submitForm,
      context.toForgotPassword,
      context.error,
      context.isPending,
    ],
  );
  // Amplify refuses the redirect itself when its OAuth config is missing; the
  // machine never hears about that either.
  const [ssoError, setSsoError] = useState<string | null>(null);

  const { control, handleSubmit } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { username: "", password: "" },
  });

  const cognitoConfig: CognitoConfigRecord = RelayEnvironment.getStore()
    .getSource()
    .get("Cognito");
  const samlProviders = getSamlProviders(cognitoConfig);

  const onValid = ({ username, password }: SignInValues) => {
    submitForm({ username, password });
  };

  const startSso = (idpId: string) => {
    setSsoError(null);
    fedSignIn(idpId).catch((err: unknown) => {
      // Another tab signed in meanwhile. The tokens sit in cookies this tab
      // shares, so v6 refuses to start a second sign-in (v5 just redirected and
      // Cognito bounced straight back). Reloading is all the Authenticator
      // needs to find that session and carry on into the app.
      if (isAlreadySignedIn(err)) {
        window.location.reload();
        return;
      }
      Sentry.captureException(err);
      setSsoError("Single sign-on didn’t start. Please try again.");
    });
  };

  // Cognito's errors stay form-level: `prevent_user_existence_errors` is on, so
  // "Incorrect username or password" must not be pinned to either field.
  const message = typeof error === "string" && error ? error : null;

  return (
    <AuthScreenLayout
      subtitle="Select how you’d like to continue."
      title="Sign in"
    >
      {/* The notice outlives the callback that raised it, so a fresher failure goes first. */}
      {(ssoError ?? notice) ? (
        <p className="text-label-medium text-foreground-negative" role="alert">
          {ssoError ?? notice}
        </p>
      ) : null}

      {samlProviders.length > 0 ? (
        <div className="flex flex-col gap-md">
          {samlProviders.map((provider) => {
            // Strip a redundant "Sign in with " some IdPs bake into display_name.
            const name = (provider.display_name || provider.name).replace(
              /^sign in with\s+/i,
              "",
            );

            return (
              <Button
                className="w-full justify-between rounded-sm border border-outline-brand bg-background-primary px-lg py-lg"
                data-testid={provider.idp_id}
                key={provider.idp_id}
                onPress={() => startSso(provider.idp_id)}
                variant="plain"
              >
                <div className="flex items-center gap-xs">
                  <KeyIcon className="size-icon-l" />
                  <span className="truncate">{`Continue with ${name}`}</span>
                </div>
                <Chip color="grey">SAML</Chip>
              </Button>
            );
          })}
        </div>
      ) : null}

      <div className="flex items-center gap-md">
        <Divider className="flex-1" />
        <span className="text-label-small text-text-secondary">
          email &amp; password
        </span>
        <Divider className="flex-1" />
      </div>

      <form
        className="flex flex-col gap-lg"
        onSubmit={handleSubmit(onValid)}
        noValidate
      >
        <Controller
          control={control}
          name="username"
          render={({ field, fieldState }) => (
            <TextField
              assistiveText={fieldState.error?.message}
              inputProps={{
                autoComplete: "username",
                placeholder: "you@company.com or username",
                className: "bg-background-primary",
              }}
              isInvalid={fieldState.invalid}
              label="Email or username"
              type="text"
              isRequired
              {...field}
            />
          )}
        />

        <div className="flex flex-col gap-[.15rem]">
          <div className="flex items-center justify-between">
            <span className="text-label-small text-text-secondary">
              Password
              <span
                aria-hidden="true"
                className="ml-0-5 text-label-small text-foreground-negative"
              >
                *
              </span>
            </span>
            <Button
              className="h-auto p-0 font-normal"
              onPress={toForgotPassword}
              variant="link"
            >
              Reset Password
            </Button>
          </div>
          <Controller
            control={control}
            name="password"
            render={({ field, fieldState }) => (
              <SecretTextField
                aria-label="Password"
                assistiveText={fieldState.error?.message}
                inputProps={{
                  autoComplete: "current-password",
                  placeholder: "••••••••",
                  className: "bg-background-primary",
                }}
                isInvalid={fieldState.invalid}
                label=""
                isRequired
                {...field}
              />
            )}
          />
        </div>

        {message ? (
          <p
            className="text-label-medium text-foreground-negative"
            role="alert"
          >
            {message}
          </p>
        ) : null}

        <Button
          className="flex items-center justify-center"
          data-testid="sign-in-submit"
          isPending={isPending}
          type="submit"
          variant="primary"
        >
          Sign In
        </Button>
      </form>
    </AuthScreenLayout>
  );
}
